import React, { useState } from 'react';
import { PinDotIcon } from './Icons';

interface DocumentationImage {
  id: number;
  src: string;
  alt: string;
  title: string;
}

const DocumentationGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<DocumentationImage | null>(null);

  const images: DocumentationImage[] = [
    {
      id: 1,
      src: '/documentation-1.png',
      alt: 'صورة توثيق المساهمة الأولى',
      title: 'توثيق المرحلة الأولى'
    },
    {
      id: 2,
      src: '/documentation-2.png',
      alt: 'صورة توثيق المساهمة الثانية مقر مؤسسة الملك عبد الله  بن عبد العزيز لواليه. في مجمع الاسكان',
      title: 'توثيق المرحلة الثانية'
    },
    {
      id: 3,
      src: '/documentation-3.png',
      alt: 'صورة توثيق لحظات التسليم الميداني',
      title: 'توثيق توزيع إضافي'
    },
    {
      id: 4,
      src: '/documentation-4.png',
      alt: 'صورة المتابعة أثناء التوزيع',
      title: 'متابعة التوزيع'
    },
    {
      id: 5,
      src: '/documentation-5.png',
      alt: 'صورة تنظيم السلال الغذائية في الموقع',
      title: 'تنظيم السلال'
    },
    {
      id: 6,
      src: '/documentation-6.png',
      alt: 'صورة من أرشيف التوزيع الميداني',
      title: 'أرشيف التوزيع'
    }
  ];

  return (
    <>
      <section className="relative py-16 sm:py-18 md:py-20 overflow-hidden rounded-4xl border border-brand-soft bg-brand-glass shadow-brand-card">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(200,157,58,0.16),_transparent_55%)]"></div>
          <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-white/45 via-transparent to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 mb-12 lg:mb-14">
            <div className="space-y-6 max-w-3xl">
              <div className="inline-flex items-center gap-3 rounded-full bg-brand-glass border border-brand-soft shadow-brand-card px-5 py-2">
                <PinDotIcon className="icon-dot" title="أرشيف" />
                <span className="text-sm font-medium text-brand-muted">أرشيف التوثيق المرئي للمبادرة</span>
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-brand-primary leading-tight">
                  صور ومشاهد من توزيع السلال الغذائية
                </h2>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-brand-muted">
                  تعرض هذه المجموعة اللحظات التوثيقية للمساهمات داخل مجمع إسكان الملك عبدالله بديحمة كتجربة بصرية تُظهر مسار التوزيع من التنظيم وحتى تسليم الأسر المستفيدة.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-brand-secondary">
                <span className="px-3 py-1.5 sm:px-3.5 sm:py-2 bg-brand-glass border border-brand-soft rounded-full shadow-brand-card">توزيع ميداني مباشر</span>
                <span className="px-3 py-1.5 sm:px-3.5 sm:py-2 bg-brand-glass border border-brand-soft rounded-full shadow-brand-card">توثيق مرئي</span>
                <span className="px-3 py-1.5 sm:px-3.5 sm:py-2 bg-brand-glass border border-brand-soft rounded-full shadow-brand-card">تغطية لجميع المراحل</span>
              </div>
            </div>
            <div className="w-full lg:w-auto">
              <div className="relative overflow-hidden rounded-3xl border border-brand-soft bg-white/86 shadow-brand-card px-6 sm:px-8 py-7 sm:py-10">
                <div className="absolute -top-14 -right-10 h-32 sm:h-36 w-32 sm:w-36 rounded-full" style={{ background: 'radial-gradient(circle, rgba(200,157,58,0.4), transparent)' }}></div>
                <div className="absolute -bottom-16 -left-12 h-36 sm:h-40 w-36 sm:w-40 rounded-full" style={{ background: 'radial-gradient(circle, rgba(30,109,59,0.28), transparent)' }}></div>
                <div className="relative z-10 space-y-4 text-brand-muted">
                  <h3 className="text-lg sm:text-xl font-semibold text-brand-primary">نطاق التغطية</h3>
                  <p className="leading-relaxed text-sm sm:text-base">
                    تم التقاط الصور خلال مراحل التوزيع الرئيسية داخل المجمع، مع شمول لحظات دقيقة تُبرز نظم التهيئة والمتابعة والتوثيق للأسر المستفيدة.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="rounded-2xl bg-brand-glass border border-brand-soft p-4 shadow-brand-card">
                      <p className="text-brand-muted">عدد الصور</p>
                      <p className="text-lg font-semibold text-brand-primary">6 صور</p>
                    </div>
                    <div className="rounded-2xl bg-brand-glass border border-brand-soft p-4 shadow-brand-card">
                      <p className="text-brand-muted">الفترة الزمنية</p>
                      <p className="text-lg font-semibold text-brand-primary">يونيو - أكتوبر 2025</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-7 lg:gap-9">
            {images.map((image) => (
              <figure
                key={image.id}
                className="group relative overflow-hidden rounded-3xl border border-brand-soft bg-brand-glass shadow-brand-card transition-all duration-700 hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 sm:gap-3 rounded-full bg-white/85 px-3 sm:px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold text-brand-primary shadow-lg backdrop-blur">
                    <span className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, var(--brand-secondary), var(--brand-primary))', color: '#fff' }}>
                      {image.id}
                    </span>
                    <span>صورة توثيق</span>
                  </div>
                </div>
                <figcaption className="relative space-y-2 px-4 sm:px-5 py-4 sm:py-5">
                  {image.title && (
                    <h3 className="text-base sm:text-lg font-semibold text-brand-primary">{image.title}</h3>
                  )}
                  <p className="text-xs sm:text-sm text-brand-muted flex items-center gap-2">
                    <PinDotIcon className="icon-dot" title={`صورة رقم ${image.id}`} />
                    صورة رقم {image.id}
                  </p>
                  <p className="text-xs sm:text-sm leading-relaxed text-brand-muted">
                    {image.alt}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-12 md:mt-16 flex flex-col lg:flex-row items-center justify-between gap-4 sm:gap-5 md:gap-6 rounded-3xl border border-dashed border-brand-soft bg-brand-glass px-5 sm:px-7 md:px-8 py-5 sm:py-6">
            <p className="text-sm sm:text-base text-brand-muted text-center lg:text-right">
              يمكن تحميل أي صورة بالحجم الكامل للاحتفاظ بالتوثيق ضمن أرشيف المتابعة والتقارير.
            </p>
            <button
              className="inline-flex items-center gap-2 sm:gap-3 rounded-full bg-brand-secondary px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm font-medium text-white shadow-brand-card transition-transform hover:-translate-y-0.5"
              onClick={() => setSelectedImage(images[0])}
              style={{
                color: "#eab305"
              }}>
              معاينة فورية لأولى الصور
            </button>
          </div>
        </div>
      </section>
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              onClick={() => setSelectedImage(null)}
              aria-label="إغلاق"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex flex-col lg:flex-row max-h-[90vh]">
              <div className="lg:w-2/3 p-4 sm:p-5 lg:p-10 flex items-center justify-center bg-gray-50">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-[60vh] sm:max-h-[65vh] lg:max-h-[72vh] object-contain rounded-lg shadow-lg"
                />
              </div>

              <div className="lg:w-1/3 p-5 sm:p-7 lg:p-9 flex flex-col justify-between bg-white">
                <div className="space-y-4 text-gray-700">
                  <div className="inline-flex items-center gap-3 rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-600">
                    <span className="h-2 w-2 rounded-full bg-gray-500"></span>
                    تفاصيل التوثيق
                  </div>
                  <div className="space-y-3">
                    {selectedImage.title && (
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedImage.title}</h3>
                    )}
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-900">رقم الصورة:</span> {selectedImage.id}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {selectedImage.alt}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-900">المشروع:</span> توزيع السلال الغذائية - إسكان الملك عبدالله
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-900">الموقع:</span> بديحمة
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <button
                    className="w-full bg-brand-secondary text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-secondary/90 transition-colors shadow-brand-card"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedImage.src;
                      link.download = `documentation-${selectedImage.id}.png`;
                      link.click();
                    }}
                  >
                    تحميل الصورة بالحجم الكامل
                  </button>
                  <button
                    className="w-full bg-gray-200 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                    onClick={() => setSelectedImage(null)}
                  >
                    إغلاق
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentationGallery;
